import React from "react";
import { Icon, IconButton } from "../../components/ui";
import { byFormat, change, int, moneyShort, pct } from "../format";
import { BarChart, Progress } from "../components/charts";
import { Avatar, DashCard, Delta, InfoTip, StatGrid } from "../components/parts";
import { cardState } from "../components/cardState";

// Reports tab — last complete week / month vs the one before (preview layout):
// a ruled row of KPI tiles with deltas, the comparison bar chart + highlights,
// then actions done on time (by SLA) and the rep scorecard table.

// Lower is better for these (time to close).
const INVERT = new Set(["hours"]);

function KpiDelta({ k }) {
  if (k.prev == null || k.value == null) return <span className="dash-kpi__muted">no comparison</span>;
  if (k.format === "pct") return <Delta value={Number(k.value) - Number(k.prev)} suffix=" pts" />;
  if (k.format === "int") return <Delta value={Number(k.value) - Number(k.prev)} suffix="" digits={0} />;
  const c = change(k.value, k.prev);
  return c == null ? <span className="dash-kpi__muted">none before</span> : <Delta value={c} invert={INVERT.has(k.format)} />;
}

// The SLAs the API can measure from history (shown when nothing was due).
const SLAS = [
  ["Quotes", "First quote within 24h of the request"],
  ["Payments", "Deposit to fully paid within 14 days"],
  ["Invoices", "Xero invoice within 2 days"],
  ["Purchase orders", "Xero PO within 2 days of agreeing"],
  ["Listing reviews", "Needs-review cleared within 24h"],
  ["Dashboard actions", "Done by its due time"],
];

function OnTime({ q }) {
  const rows = q.data?.actions_on_time || [];
  const state = cardState(q, { what: "actions on time" });
  return (
    <DashCard title="Actions done on time" info="onTime" meta="by SLA">
      {state ||
        (rows.length ? (
          <div className="dash-ontime">
            {rows.map((a) => {
              const v = Number(a.pct) || 0;
              return (
                <div className="dash-ontime__row" key={a.type}>
                  <div className="dash-ontime__head">
                    <span className="dash-ontime__name">{a.label || a.type}</span>
                    <b className={`dash-num ${v >= 80 ? "dash-good" : v < 50 ? "dash-bad" : ""}`}>{pct(a.pct)}</b>
                  </div>
                  <Progress value={v} label={`${a.label || a.type} on time`} />
                  <span className="dash-ontime__sla">
                    {a.sla_label ? `${a.sla_label} · ` : ""}
                    <span className="dash-num">{int(a.on_time)} of {int(a.done ?? a.total)}</span> on time
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="dash-ontime__empty">
            <p className="dash-empty">Nothing tracked was done in this period.</p>
            <div className="dash-muted">Tracked:</div>
            <ul className="dash-sla-list">
              {SLAS.map(([n, s]) => <li key={n}><b>{n}</b> — {s}</li>)}
            </ul>
          </div>
        ))}
    </DashCard>
  );
}

function Scorecard({ q, label }) {
  const reps = [...(q.data?.reps || [])].sort((a, b) => (Number(b.turnover_pence) || 0) - (Number(a.turnover_pence) || 0));
  const total = reps.reduce((s, r) => s + (Number(r.turnover_pence) || 0), 0);
  const state = cardState(q, { what: "the scorecard", empty: !reps.length, emptyText: "No rep activity in this period." });
  return (
    <DashCard title="Rep scorecard" info="team" meta={label}>
      {state || (
        <div className="dash-table-wrap">
          <table className="dash-table dash-table--compact">
            <thead>
              <tr>
                <th scope="col">Rep</th>
                <th scope="col">Turnover</th>
                <th scope="col">Share</th>
                <th scope="col">Won</th>
              </tr>
            </thead>
            <tbody>
              {reps.map((r, i) => {
                const share = total ? ((Number(r.turnover_pence) || 0) / total) * 100 : 0;
                return (
                  <tr key={r.user_id}>
                    <td>
                      <span className="dash-who">
                        <span className="dash-rank-n dash-num">{i + 1}</span>
                        <Avatar name={r.name} i={i} />
                        {r.name}
                      </span>
                    </td>
                    <td className="dash-num">{moneyShort(r.turnover_pence)}</td>
                    <td className="dash-num">
                      {total ? pct(share) : "—"}
                      <span className="dash-mbar" aria-hidden="true"><Progress value={share} /></span>
                    </td>
                    <td className="dash-num">{int(r.won)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashCard>
  );
}

export default function ReportsTab({ q, period, offset, setOffset }) {
  const d = q.data || {};
  const p = d.period || {};
  const head = cardState(q, { what: "the report", lines: 3 });
  const series = d.series || {};
  const kpis = d.kpis || [];
  return (
    <div className="dash-stack dash-stack--page">
      <DashCard
        title={
          <>
            {p.label || (period === "week" ? "Last week" : "Last month")}
            {p.range ? <span className="dash-card__meta dash-card__meta--inline">{p.range}</span> : null}
            <InfoTip k="reports" />
          </>
        }
        meta={p.prev_label ? `vs ${p.prev_label}` : null}
        tools={
          <span className="dash-stepper">
            <IconButton label={`Previous ${period}`} size="sm" variant="ghost" disabled={offset >= 24} onClick={() => setOffset(Math.min(24, offset + 1))}>
              <Icon name="ChevronLeft" size="sm" />
            </IconButton>
            <IconButton label={`Next ${period}`} size="sm" variant="ghost" disabled={offset <= 1} onClick={() => setOffset(Math.max(1, offset - 1))}>
              <Icon name="ChevronRight" size="sm" />
            </IconButton>
          </span>
        }
      >
        {head || (
          <StatGrid className="dash-stats--row dash-stats--report">
            {kpis.map((k) => (
              <div className="dash-stat" key={k.key}>
                <span className="dash-stat__l">{k.label}</span>
                <span className="dash-stat__v dash-num">{byFormat(k.value, k.format)}</span>
                <span className="dash-stat__f"><KpiDelta k={k} /></span>
              </div>
            ))}
          </StatGrid>
        )}
      </DashCard>
      <div className="dash-grid dash-grid--main dash-grid--eq">
        <DashCard
          title={period === "week" ? "Turnover by day" : "Turnover by week"}
          meta={
            <span className="dash-key">
              <span><i className="dash-key__sw dash-bg--c1" /> {p.label || "This period"}</span>
              <span><i className="dash-key__sw dash-bg--c6" /> {p.prev_label || "Previous"}</span>
            </span>
          }
        >
          {cardState(q, { what: "the chart", shape: "card", empty: !(series.labels || []).length, emptyText: "No turnover in this period." }) || (
            <BarChart values={series.current || []} previous={series.previous || []} labels={series.labels || []} format={moneyShort} label="Turnover, this period vs previous" height={240} />
          )}
        </DashCard>
        <DashCard title="Highlights">
          {cardState(q, { what: "highlights", empty: !(d.highlights || []).length, emptyText: "No highlights for this period." }) || (
            <div className="dash-list">
              {d.highlights.map((h) => (
                <div className="dash-li dash-li--kv" key={h.label}>
                  <div className="dash-li__s">{h.label}</div>
                  <div className="dash-li__r dash-li__r--wrap">
                    {h.value}
                    {h.amount_pence != null && !String(h.value).includes("£") ? <span className="dash-subtle"> · {moneyShort(h.amount_pence)}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DashCard>
      </div>
      <div className="dash-grid dash-grid--2 dash-grid--eq">
        <OnTime q={q} />
        <Scorecard q={q} label={p.label} />
      </div>
    </div>
  );
}
